import { ReactNode } from 'react';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BusinessIcon from '@material-ui/icons/Business';
import PublicIcon from '@material-ui/icons/Public';
import FavoriteIcon from '@material-ui/icons/Favorite';
import NetworkCheckIcon from '@material-ui/icons/NetworkCheck';
import BarChartIcon from '@material-ui/icons/BarChart';
import LinkIcon from '@material-ui/icons/Link';
import SchoolIcon from '@material-ui/icons/School';
import GroupIcon from '@material-ui/icons/Group';
import BuildIcon from '@material-ui/icons/Build';
import DescriptionIcon from '@material-ui/icons/Description';
import DashboardIcon from '@material-ui/icons/Dashboard';
import StarIcon from '@material-ui/icons/Star';
import SecurityIcon from '@material-ui/icons/Security';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ImageIcon from '@material-ui/icons/Image';
import WebIcon from '@material-ui/icons/Web';
import NotificationsIcon from '@material-ui/icons/Notifications';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import HorizontalSplitIcon from '@material-ui/icons/HorizontalSplit';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import RemoveIcon from '@material-ui/icons/Remove';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import DataUsageIcon from '@material-ui/icons/DataUsage';
import TableChartIcon from '@material-ui/icons/TableChart';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import PieChartIcon from '@material-ui/icons/PieChart';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
// `BarChartIcon` is already imported at the top of this file (used
// by the icon picker registry). We re-use that same import for the
// api-bar-chart gallery icon rather than re-importing under the same
// name, which would be a duplicate symbol.
import { CatalogStats } from '../../components/home/CatalogStats';
import { WelcomeBanner } from '../../components/home/WelcomeBanner';
import { CatalogKindsGuide } from '../../components/home/CatalogKindsGuide';
import { QuickLinkCard } from '../../components/home/QuickLinkCard';
import { MarkdownBlock } from '../../components/contentPages/MarkdownBlock';
import { SectionHeader } from '../../components/contentPages/SectionHeader';
import {
  Announcement,
  AnnouncementSeverity,
} from '../../components/contentPages/Announcement';
import { IframeEmbed } from '../../components/contentPages/IframeEmbed';
import { ImageBlock } from '../../components/contentPages/ImageBlock';
import { EntityList } from '../../components/contentPages/EntityList';
import { LinkGrid, LinkGridItem } from '../../components/contentPages/LinkGrid';
import {
  StatCard,
  StatCardTone,
} from '../../components/contentPages/StatCard';
import { TeamMembers } from '../../components/contentPages/TeamMembers';
import {
  Divider as ContentDivider,
  DividerSpacing,
  DividerStyle,
} from '../../components/contentPages/Divider';
import { YouTubeVideo } from '../../components/contentPages/YouTubeVideo';
import { Quote } from '../../components/contentPages/Quote';
import {
  ApiStatCard,
} from '../../components/contentPages/api/ApiStatCard';
import { ApiJsonViewer } from '../../components/contentPages/api/ApiJsonViewer';
import {
  ApiTable,
  ApiTableColumn,
} from '../../components/contentPages/api/ApiTable';
import { ApiBarChart } from '../../components/contentPages/api/ApiBarChart';
import { ApiLineChart } from '../../components/contentPages/api/ApiLineChart';
import { ApiPieChart } from '../../components/contentPages/api/ApiPieChart';
import { ChartSeries } from '../../components/contentPages/api/chartData';
import { RecentNotificationsWidget } from '../../components/contentPages/RecentNotificationsWidget';

/**
 * Icons admins can pick for the sidebar entry of a `ContentPage` AND
 * for the `quick-link-card` / `link-grid` widgets. Keep the keys
 * stable — the Scaffolder template references each one by name and
 * the YAML persisted to the catalog stores the picked key, so
 * renaming a key silently breaks existing content pages.
 *
 * When adding a new icon:
 *   1) Add the import above (Material UI v4 icon set — same package
 *      already used across the app, no new dep needed).
 *   2) Register it here with a kebab-case key.
 *   3) Mirror the new key in the `iconPicker` option list down below
 *      so the WidgetGallery dropdown picks it up automatically.
 */
export const ICONS: Record<string, ReactNode> = {
  'library-books': <LibraryBooksIcon />,
  'menu-book': <MenuBookIcon />,
  business: <BusinessIcon />,
  public: <PublicIcon />,
  favorite: <FavoriteIcon />,
  'network-check': <NetworkCheckIcon />,
  'bar-chart': <BarChartIcon />,
  link: <LinkIcon />,
  school: <SchoolIcon />,
  group: <GroupIcon />,
  build: <BuildIcon />,
  description: <DescriptionIcon />,
  dashboard: <DashboardIcon />,
  star: <StarIcon />,
  security: <SecurityIcon />,
  assessment: <AssessmentIcon />,
};

/** Sorted list of icon keys for use by select-style form fields. */
export const ICON_KEYS = Object.keys(ICONS).sort();

/** Resolve a stored icon name back to a React element, falling back
 *  to a generic `description` icon so a removed key NEVER crashes the
 *  whole page (just renders a neutral icon next to the title). */
export const resolveIcon = (name: string | undefined): ReactNode => {
  if (name && ICONS[name]) return ICONS[name];
  return ICONS.description;
};

/* -------------------------------------------------------------------------- */
/* Config schema primitives                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A widget's `configSchema` is a small declarative description of the
 * fields the admin sees in the WidgetGallery's inline editor. The
 * editor (`WidgetEditor.tsx`) walks the array and renders a Material
 * UI control for each entry — no JSON Schema in the middle, no
 * RJSF-inside-RJSF.
 *
 * Why a hand-rolled mini-schema instead of leveraging JSON Schema
 * directly:
 *   - JSON Schema doesn't have first-class concepts for "pick an icon
 *     from a visual gallery" or "pick a Bosch palette swatch". With
 *     this enum we get those without bending JSON Schema or the rjsf
 *     widget vocabulary.
 *   - The set of UI primitives stays explicit and short — 8 kinds is
 *     enough for every widget in the registry; adding a new one means
 *     adding a renderer in WidgetEditor and a sample.
 *
 * Trade-off: each new field kind costs ~30 LOC in the editor and a
 * matching renderer. Don't grow this enum casually.
 */
export type ConfigFieldKind =
  | { kind: 'string'; placeholder?: string }
  | { kind: 'text'; placeholder?: string; rows?: number }
  | { kind: 'url'; placeholder?: string }
  | { kind: 'select'; options: { value: string; label: string }[] }
  | { kind: 'iconPicker' }
  | { kind: 'colorPicker' }
  | { kind: 'boolean' }
  | { kind: 'number'; min?: number; max?: number }
  | {
      kind: 'list';
      itemLabel: string;
      itemSchema: ConfigField[];
      itemSample: Record<string, unknown>;
      maxItems?: number;
    };

export type ConfigField = {
  key: string;
  label: string;
  description?: string;
  required?: boolean;
} & ConfigFieldKind;

/**
 * Public widget contract.
 *
 * Three configs per widget, all serving distinct purposes:
 *
 *   - `demoConfig`   → shown ONLY in the WidgetGallery palette as a
 *                      thumbnail preview. Generic / lorem-ipsum-ish
 *                      content so the admin sees what the widget looks
 *                      like before adding it. NEVER ends up in the
 *                      stored YAML.
 *   - `sampleConfig` → seeded into `spec.widgets` when the admin clicks
 *                      "Add to page" on the palette card. Usually
 *                      empty strings + safe defaults — the admin then
 *                      fills the real values via the inline editor.
 *   - `configSchema` → drives the inline editor's controls (text,
 *                      iconPicker, etc.). Validation happens both
 *                      client-side (in WidgetEditor) and server-side
 *                      (rjsf re-validates on form submit).
 */
export type WidgetDefinition = {
  label: string;
  description: string;
  /** Icon shown on the palette card AND on the editor's drag handle. */
  galleryIcon: ReactNode;
  /** Demo-mode preview rendered inside the palette card. */
  demoConfig: Record<string, unknown>;
  /** Initial config inserted when the admin "adds" the widget. */
  sampleConfig: Record<string, unknown>;
  /** Editor schema. Empty array = widget takes no config. */
  configSchema: ConfigField[];
  /** Renderer — receives EITHER demo, sample, or admin-filled config. */
  render: (config: Record<string, unknown> | undefined) => ReactNode;
};

/**
 * Grid widths a widget can occupy on the page.
 *
 * The numbers map 1:1 to Material UI's `Grid item md={n}` (12-column
 * grid). At `xs` (mobile) every widget collapses to full width
 * regardless of this value — stacking a 1/4 stat card next to a
 * 1/2 markdown block on a phone produces unreadable text.
 *
 * Why these EIGHT values and not "any number 1-12":
 *
 *   - The set covers every "nice" fraction (1/4, 1/3, 1/2, 2/3, 3/4,
 *     full) plus 5 (≈ 40%) and 7 (≈ 60%) for the rare case an admin
 *     wants asymmetric pairs like 5+7. Anything more exotic (e.g.
 *     `2 + 10`) reads as a "widget glued to the page margin" and we
 *     prefer to nudge admins toward balanced rows.
 *   - Restricting to a known set lets the width selector be a tiny
 *     segmented control instead of a free number input, and keeps the
 *     YAML stored in git pleasantly readable (`width: 6` vs
 *     `width: 7.3145`).
 */
export type WidgetWidth = 3 | 4 | 5 | 6 | 7 | 8 | 9 | 12;

/** Map of width values to human-readable labels (used by the gallery
 *  selector AND the per-item chip in the selected list). */
export const WIDTH_LABELS: Record<WidgetWidth, string> = {
  12: 'Full',
  9: '3/4',
  8: '2/3',
  7: '7/12',
  6: '1/2',
  5: '5/12',
  4: '1/3',
  3: '1/4',
};

/** Returns a valid `WidgetWidth` from an arbitrary value, falling
 *  back to 12 (full width) for missing / malformed inputs. This is
 *  the single normaliser used by every renderer so the rest of the
 *  code can treat width as guaranteed-valid. */
export const normaliseWidth = (raw: unknown): WidgetWidth => {
  if (typeof raw !== 'number') return 12;
  // Use a single switch instead of `Object.keys(WIDTH_LABELS).includes`
  // so we don't pay the cost of building a runtime array on every
  // widget render in a page that might have 20+ widgets.
  switch (raw) {
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
    case 12:
      return raw;
    default:
      return 12;
  }
};

export type WidgetSpec = {
  type: string;
  /**
   * Grid width (MD breakpoint). Omitted = full width (12). The
   * renderer wraps every widget in a `<Grid item xs={12} md={width}>`,
   * so consecutive widgets whose widths sum to ≤ 12 sit on the same
   * row; MUI handles wrapping automatically when the sum overflows.
   */
  width?: WidgetWidth;
  config?: Record<string, unknown>;
};

/* -------------------------------------------------------------------------- */
/* Renderers                                                                   */
/* -------------------------------------------------------------------------- */

const renderCatalogOverview = () => <CatalogStats />;
const renderWelcomeBanner = () => <WelcomeBanner />;
const renderKindsReference = () => <CatalogKindsGuide />;

const renderMarkdown = (cfg: Record<string, unknown> | undefined) => {
  const body = typeof cfg?.body === 'string' ? cfg.body : '';
  if (!body) {
    return (
      <MarkdownBlock body="_Empty markdown widget — edit and add your content._" />
    );
  }
  return <MarkdownBlock body={body} />;
};

const renderQuickLinkCard = (cfg: Record<string, unknown> | undefined) => {
  const {
    title = '',
    primaryText = '',
    secondaryText = '',
    actionLabel = 'Abrir',
    actionUrl = '#',
    icon,
    color,
    external,
  } = (cfg ?? {}) as Record<string, unknown>;
  return (
    <QuickLinkCard
      title={String(title)}
      primaryText={String(primaryText)}
      secondaryText={String(secondaryText)}
      actionLabel={String(actionLabel)}
      actionUrl={String(actionUrl)}
      icon={resolveIcon(typeof icon === 'string' ? icon : undefined)}
      color={
        (typeof color === 'string'
          ? color
          : 'blue') as Parameters<typeof QuickLinkCard>[0]['color']
      }
      external={Boolean(external)}
    />
  );
};

const renderSectionHeader = (cfg: Record<string, unknown> | undefined) => {
  const title = typeof cfg?.title === 'string' ? cfg.title : '';
  const subtitle =
    typeof cfg?.subtitle === 'string' ? cfg.subtitle : undefined;
  return <SectionHeader title={title || 'Untitled section'} subtitle={subtitle} />;
};

const renderAnnouncement = (cfg: Record<string, unknown> | undefined) => {
  const severity =
    typeof cfg?.severity === 'string'
      ? (cfg.severity as AnnouncementSeverity)
      : 'info';
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const body = typeof cfg?.body === 'string' ? cfg.body : undefined;
  return <Announcement severity={severity} title={title} body={body} />;
};

const renderIframeEmbed = (cfg: Record<string, unknown> | undefined) => {
  const url = typeof cfg?.url === 'string' ? cfg.url : '';
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const height =
    typeof cfg?.height === 'number' ? cfg.height : undefined;
  return <IframeEmbed url={url} title={title} height={height} />;
};

const renderImageBlock = (cfg: Record<string, unknown> | undefined) => {
  const src = typeof cfg?.src === 'string' ? cfg.src : '';
  const alt = typeof cfg?.alt === 'string' ? cfg.alt : undefined;
  const caption =
    typeof cfg?.caption === 'string' ? cfg.caption : undefined;
  const maxWidth =
    typeof cfg?.maxWidth === 'number' ? cfg.maxWidth : undefined;
  return (
    <ImageBlock src={src} alt={alt} caption={caption} maxWidth={maxWidth} />
  );
};

const renderEntityList = (cfg: Record<string, unknown> | undefined) => {
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const kind = typeof cfg?.kind === 'string' ? cfg.kind : 'Component';
  const type = typeof cfg?.type === 'string' ? cfg.type : undefined;
  const owner = typeof cfg?.owner === 'string' ? cfg.owner : undefined;
  const limit =
    typeof cfg?.limit === 'number' ? cfg.limit : undefined;
  return (
    <EntityList
      title={title}
      kind={kind}
      type={type}
      owner={owner}
      limit={limit}
    />
  );
};

const renderLinkGrid = (cfg: Record<string, unknown> | undefined) => {
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const columns =
    typeof cfg?.columns === 'number' && [2, 3, 4].includes(cfg.columns)
      ? (cfg.columns as 2 | 3 | 4)
      : 4;
  const links = Array.isArray(cfg?.links)
    ? (cfg!.links as LinkGridItem[])
    : [];
  return <LinkGrid title={title} columns={columns} links={links} />;
};

const renderStatCard = (cfg: Record<string, unknown> | undefined) => {
  const label = typeof cfg?.label === 'string' ? cfg.label : '';
  const value =
    typeof cfg?.value === 'number' || typeof cfg?.value === 'string'
      ? cfg.value
      : '';
  const unit = typeof cfg?.unit === 'string' ? cfg.unit : undefined;
  const delta = typeof cfg?.delta === 'string' ? cfg.delta : undefined;
  const tone: StatCardTone =
    cfg?.tone === 'success' ||
    cfg?.tone === 'warning' ||
    cfg?.tone === 'error'
      ? cfg.tone
      : 'neutral';
  return <StatCard label={label} value={value} unit={unit} delta={delta} tone={tone} />;
};

const renderTeamMembers = (cfg: Record<string, unknown> | undefined) => {
  const groupRef =
    typeof cfg?.groupRef === 'string' ? cfg.groupRef : '';
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const showEmail = Boolean(cfg?.showEmail);
  return <TeamMembers groupRef={groupRef} title={title} showEmail={showEmail} />;
};

const renderDivider = (cfg: Record<string, unknown> | undefined) => {
  const spacing: DividerSpacing =
    cfg?.spacing === 'tight' || cfg?.spacing === 'loose'
      ? cfg.spacing
      : 'normal';
  const style: DividerStyle =
    cfg?.style === 'dashed' ? 'dashed' : 'solid';
  return <ContentDivider spacing={spacing} style={style} />;
};

const renderYouTubeVideo = (cfg: Record<string, unknown> | undefined) => {
  const videoId =
    typeof cfg?.videoId === 'string' ? cfg.videoId : '';
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const startSeconds =
    typeof cfg?.startSeconds === 'number' ? cfg.startSeconds : undefined;
  return (
    <YouTubeVideo
      videoId={videoId}
      title={title}
      startSeconds={startSeconds}
    />
  );
};

const renderQuote = (cfg: Record<string, unknown> | undefined) => {
  const text = typeof cfg?.text === 'string' ? cfg.text : '';
  const author = typeof cfg?.author === 'string' ? cfg.author : undefined;
  const role = typeof cfg?.role === 'string' ? cfg.role : undefined;
  return <Quote text={text} author={author} role={role} />;
};

/* ---- api-* widgets ----------------------------------------------------
 *
 * All `api-*` widgets share the same data-loading config shape. Rather
 * than duplicating type guards in every renderer, we keep them inline
 * but factored: each renderer takes only the unknown record and pulls
 * out its strongly-typed properties before delegating to a typed
 * component prop interface.
 */

const renderApiStatCard = (cfg: Record<string, unknown> | undefined) => {
  const label = typeof cfg?.label === 'string' ? cfg.label : '';
  const url = typeof cfg?.url === 'string' ? cfg.url : undefined;
  const inlineJson =
    typeof cfg?.inlineJson === 'string' ? cfg.inlineJson : undefined;
  const valuePath =
    typeof cfg?.valuePath === 'string' ? cfg.valuePath : '';
  const deltaPath =
    typeof cfg?.deltaPath === 'string' ? cfg.deltaPath : undefined;
  const unit = typeof cfg?.unit === 'string' ? cfg.unit : undefined;
  const tone =
    cfg?.tone === 'success' ||
    cfg?.tone === 'warning' ||
    cfg?.tone === 'error'
      ? cfg.tone
      : 'neutral';
  const refreshSeconds =
    typeof cfg?.refreshSeconds === 'number'
      ? cfg.refreshSeconds
      : undefined;
  return (
    <ApiStatCard
      label={label}
      url={url}
      inlineJson={inlineJson}
      valuePath={valuePath}
      deltaPath={deltaPath}
      unit={unit}
      tone={tone}
      refreshSeconds={refreshSeconds}
    />
  );
};

const renderApiJsonViewer = (
  cfg: Record<string, unknown> | undefined,
) => {
  return (
    <ApiJsonViewer
      title={typeof cfg?.title === 'string' ? cfg.title : undefined}
      url={typeof cfg?.url === 'string' ? cfg.url : undefined}
      inlineJson={
        typeof cfg?.inlineJson === 'string' ? cfg.inlineJson : undefined
      }
      path={typeof cfg?.path === 'string' ? cfg.path : undefined}
      refreshSeconds={
        typeof cfg?.refreshSeconds === 'number'
          ? cfg.refreshSeconds
          : undefined
      }
    />
  );
};

const renderApiTable = (cfg: Record<string, unknown> | undefined) => {
  const columns = Array.isArray(cfg?.columns)
    ? (cfg!.columns as ApiTableColumn[])
    : [];
  return (
    <ApiTable
      title={typeof cfg?.title === 'string' ? cfg.title : undefined}
      url={typeof cfg?.url === 'string' ? cfg.url : undefined}
      inlineJson={
        typeof cfg?.inlineJson === 'string' ? cfg.inlineJson : undefined
      }
      path={typeof cfg?.path === 'string' ? cfg.path : undefined}
      columns={columns}
      refreshSeconds={
        typeof cfg?.refreshSeconds === 'number'
          ? cfg.refreshSeconds
          : undefined
      }
      maxRows={
        typeof cfg?.maxRows === 'number' ? cfg.maxRows : undefined
      }
    />
  );
};

const renderApiBarChart = (cfg: Record<string, unknown> | undefined) => {
  const series = Array.isArray(cfg?.series)
    ? (cfg!.series as ChartSeries[])
    : [];
  return (
    <ApiBarChart
      title={typeof cfg?.title === 'string' ? cfg.title : undefined}
      url={typeof cfg?.url === 'string' ? cfg.url : undefined}
      inlineJson={
        typeof cfg?.inlineJson === 'string' ? cfg.inlineJson : undefined
      }
      path={typeof cfg?.path === 'string' ? cfg.path : undefined}
      xKey={typeof cfg?.xKey === 'string' ? cfg.xKey : ''}
      series={series}
      stacked={Boolean(cfg?.stacked)}
      refreshSeconds={
        typeof cfg?.refreshSeconds === 'number'
          ? cfg.refreshSeconds
          : undefined
      }
    />
  );
};

const renderApiLineChart = (
  cfg: Record<string, unknown> | undefined,
) => {
  const series = Array.isArray(cfg?.series)
    ? (cfg!.series as ChartSeries[])
    : [];
  return (
    <ApiLineChart
      title={typeof cfg?.title === 'string' ? cfg.title : undefined}
      url={typeof cfg?.url === 'string' ? cfg.url : undefined}
      inlineJson={
        typeof cfg?.inlineJson === 'string' ? cfg.inlineJson : undefined
      }
      path={typeof cfg?.path === 'string' ? cfg.path : undefined}
      xKey={typeof cfg?.xKey === 'string' ? cfg.xKey : ''}
      series={series}
      filled={Boolean(cfg?.filled)}
      smooth={Boolean(cfg?.smooth)}
      refreshSeconds={
        typeof cfg?.refreshSeconds === 'number'
          ? cfg.refreshSeconds
          : undefined
      }
    />
  );
};

const renderApiPieChart = (cfg: Record<string, unknown> | undefined) => {
  return (
    <ApiPieChart
      title={typeof cfg?.title === 'string' ? cfg.title : undefined}
      url={typeof cfg?.url === 'string' ? cfg.url : undefined}
      inlineJson={
        typeof cfg?.inlineJson === 'string' ? cfg.inlineJson : undefined
      }
      path={typeof cfg?.path === 'string' ? cfg.path : undefined}
      labelKey={typeof cfg?.labelKey === 'string' ? cfg.labelKey : ''}
      valueKey={typeof cfg?.valueKey === 'string' ? cfg.valueKey : ''}
      donut={Boolean(cfg?.donut)}
      refreshSeconds={
        typeof cfg?.refreshSeconds === 'number'
          ? cfg.refreshSeconds
          : undefined
      }
    />
  );
};

/* ---- platform-feature widgets ----------------------------------------- */

const renderRecentNotifications = (
  cfg: Record<string, unknown> | undefined,
) => {
  const title = typeof cfg?.title === 'string' ? cfg.title : undefined;
  const limit =
    typeof cfg?.limit === 'number' ? cfg.limit : undefined;
  const unreadOnly = Boolean(cfg?.unreadOnly);
  const refreshSeconds =
    typeof cfg?.refreshSeconds === 'number'
      ? cfg.refreshSeconds
      : undefined;
  const showSeeAll =
    cfg?.showSeeAll === undefined ? true : Boolean(cfg.showSeeAll);
  return (
    <RecentNotificationsWidget
      title={title}
      limit={limit}
      unreadOnly={unreadOnly}
      refreshSeconds={refreshSeconds}
      showSeeAll={showSeeAll}
    />
  );
};

/* -------------------------------------------------------------------------- */
/* Registry                                                                    */
/* -------------------------------------------------------------------------- */

const ICON_OPTIONS = ICON_KEYS.map(k => ({ value: k, label: k }));

/**
 * Single source of truth for widget types accepted on
 * `ContentPage.spec.widgets`. Adding a new widget = add an import for
 * the React component at the top of this file + add an entry here.
 *
 * Order in this object is the order widgets appear in the palette
 * (`Object.entries` preserves insertion order). The most useful
 * widgets — section header + markdown + quick link — live near the
 * top so they're the easiest to discover for a new admin.
 */
export const WIDGETS: Record<string, WidgetDefinition> = {
  'section-header': {
    label: 'Section header',
    description:
      'Large heading + optional subtitle, used to break the page into sections.',
    galleryIcon: <HorizontalSplitIcon />,
    demoConfig: {
      title: 'Section title',
      subtitle: 'Optional one-line description that explains the section below.',
    },
    sampleConfig: { title: '', subtitle: '' },
    configSchema: [
      {
        key: 'title',
        label: 'Title',
        required: true,
        kind: 'string',
        placeholder: 'e.g. Quick links',
      },
      {
        key: 'subtitle',
        label: 'Subtitle (optional)',
        kind: 'string',
        placeholder: 'Short sentence that describes the section',
      },
    ],
    render: renderSectionHeader,
  },

  divider: {
    label: 'Divider',
    description:
      'Plain horizontal rule — lighter than a section header, no text.',
    galleryIcon: <RemoveIcon />,
    demoConfig: { spacing: 'normal', style: 'solid' },
    sampleConfig: { spacing: 'normal', style: 'solid' },
    configSchema: [
      {
        key: 'spacing',
        label: 'Vertical spacing',
        kind: 'select',
        options: [
          { value: 'tight', label: 'Tight (4px)' },
          { value: 'normal', label: 'Normal (16px)' },
          { value: 'loose', label: 'Loose (32px)' },
        ],
      },
      {
        key: 'style',
        label: 'Line style',
        kind: 'select',
        options: [
          { value: 'solid', label: 'Solid' },
          { value: 'dashed', label: 'Dashed' },
        ],
      },
    ],
    render: renderDivider,
  },

  markdown: {
    label: 'Markdown',
    description:
      'Rich text block — headings, lists, tables, links, code.',
    galleryIcon: <DescriptionIcon />,
    demoConfig: {
      body: `## Markdown sample

Text with **bold**, _italics_ and [a link](#).

- item 1
- item 2

\`\`\`bash
echo "also supports code blocks"
\`\`\``,
    },
    sampleConfig: {
      body: '',
    },
    configSchema: [
      {
        key: 'body',
        label: 'Content (Markdown)',
        required: true,
        kind: 'text',
        rows: 12,
        placeholder: '# Heading\n\nSome rich text…',
        description:
          'Accepts GitHub-flavoured Markdown. External links open in a new tab automatically.',
      },
    ],
    render: renderMarkdown,
  },

  'quick-link-card': {
    label: 'Quick Link Card',
    description: 'Single colourful card with an internal or external link.',
    galleryIcon: <LinkIcon />,
    demoConfig: {
      title: 'EXAMPLE',
      primaryText: 'Quick Link',
      secondaryText: 'Descriptive sub-text appears here',
      actionLabel: 'Open →',
      actionUrl: '#',
      icon: 'link',
      color: 'blue',
      external: false,
    },
    sampleConfig: {
      title: '',
      primaryText: '',
      secondaryText: '',
      actionLabel: 'Open',
      actionUrl: '',
      icon: 'link',
      color: 'blue',
      external: false,
    },
    configSchema: [
      {
        key: 'title',
        label: 'Label (UPPERCASE)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. CONFLUENCE',
      },
      {
        key: 'primaryText',
        label: 'Primary text',
        required: true,
        kind: 'string',
        placeholder: 'e.g. Wiki',
      },
      {
        key: 'secondaryText',
        label: 'Description (one sentence)',
        kind: 'string',
      },
      {
        key: 'actionLabel',
        label: 'Link label',
        required: true,
        kind: 'string',
        placeholder: 'e.g. Open Confluence',
      },
      {
        key: 'actionUrl',
        label: 'URL',
        required: true,
        kind: 'url',
        placeholder: 'https://… or /internal-path',
      },
      { key: 'icon', label: 'Icon', kind: 'iconPicker' },
      { key: 'color', label: 'Card colour', kind: 'colorPicker' },
      {
        key: 'external',
        label: 'Open in new tab',
        kind: 'boolean',
      },
    ],
    render: renderQuickLinkCard,
  },

  'stat-card': {
    label: 'Stat Card',
    description:
      'Single KPI tile (label + big number + optional unit / delta). Pair four at width 1/4 for a hero strip.',
    galleryIcon: <TrendingUpIcon />,
    demoConfig: {
      label: 'ACTIVE COMPONENTS',
      value: '1,247',
      unit: 'svc',
      delta: '+12 this month',
      tone: 'success',
    },
    sampleConfig: {
      label: '',
      value: '',
      unit: '',
      delta: '',
      tone: 'neutral',
    },
    configSchema: [
      {
        key: 'label',
        label: 'Label (UPPERCASE)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. ACTIVE COMPONENTS',
      },
      {
        key: 'value',
        label: 'Value',
        required: true,
        kind: 'string',
        placeholder: 'e.g. 1,247 or 32%',
      },
      {
        key: 'unit',
        label: 'Unit (optional)',
        kind: 'string',
        placeholder: 'e.g. svc, %, USD',
      },
      {
        key: 'delta',
        label: 'Delta / sub-text (optional)',
        kind: 'string',
        placeholder: 'e.g. +12 this month',
        description:
          'Strings starting with "+" / "↑" get an up arrow; "-" / "↓" get a down arrow; everything else stays flat.',
      },
      {
        key: 'tone',
        label: 'Tone (colour of the delta pill)',
        kind: 'select',
        options: [
          { value: 'neutral', label: 'Neutral (gray)' },
          { value: 'success', label: 'Success (green)' },
          { value: 'warning', label: 'Warning (yellow)' },
          { value: 'error', label: 'Error (red)' },
        ],
      },
    ],
    render: renderStatCard,
  },

  'link-grid': {
    label: 'Link Grid',
    description: 'Grid with multiple link cards inside a single block.',
    galleryIcon: <ViewModuleIcon />,
    demoConfig: {
      title: 'QUICK LINKS',
      columns: 4,
      links: [
        {
          title: 'TECHDOCS',
          primaryText: 'Browse',
          secondaryText: 'Search docs',
          actionLabel: 'Open',
          actionUrl: '#',
          icon: 'library-books',
          color: 'blue',
        },
        {
          title: 'CATALOG',
          primaryText: 'Explore',
          secondaryText: 'Browse the catalog',
          actionLabel: 'Open',
          actionUrl: '#',
          icon: 'dashboard',
          color: 'blue-light',
        },
        {
          title: 'CREATE',
          primaryText: 'Templates',
          secondaryText: 'Spin up new services',
          actionLabel: 'Open',
          actionUrl: '#',
          icon: 'build',
          color: 'green',
        },
        {
          title: 'GUIDE',
          primaryText: 'Reference',
          secondaryText: 'Catalog kinds',
          actionLabel: 'Open',
          actionUrl: '#',
          icon: 'menu-book',
          color: 'gray',
        },
      ],
    },
    sampleConfig: {
      title: '',
      columns: 4,
      links: [],
    },
    configSchema: [
      {
        key: 'title',
        label: 'Group title (optional)',
        kind: 'string',
      },
      {
        key: 'columns',
        label: 'Columns per row',
        kind: 'select',
        options: [
          { value: '2', label: '2 columns (larger cards)' },
          { value: '3', label: '3 columns (medium)' },
          { value: '4', label: '4 columns (compact)' },
        ],
      },
      {
        key: 'links',
        label: 'Links',
        kind: 'list',
        itemLabel: 'Link',
        itemSample: {
          title: '',
          primaryText: '',
          secondaryText: '',
          actionLabel: 'Open',
          actionUrl: '',
          icon: 'link',
          color: 'blue',
          external: false,
        },
        itemSchema: [
          {
            key: 'title',
            label: 'Label (UPPERCASE)',
            required: true,
            kind: 'string',
          },
          {
            key: 'primaryText',
            label: 'Primary text',
            required: true,
            kind: 'string',
          },
          {
            key: 'secondaryText',
            label: 'Description',
            kind: 'string',
          },
          {
            key: 'actionLabel',
            label: 'Link label',
            kind: 'string',
          },
          {
            key: 'actionUrl',
            label: 'URL',
            required: true,
            kind: 'url',
          },
          { key: 'icon', label: 'Icon', kind: 'iconPicker' },
          { key: 'color', label: 'Colour', kind: 'colorPicker' },
          {
            key: 'external',
            label: 'External (new tab)',
            kind: 'boolean',
          },
        ],
      },
    ],
    render: renderLinkGrid,
  },

  announcement: {
    label: 'Announcement',
    description: 'Colour-coded notice (info / warning / error / success).',
    galleryIcon: <NotificationsIcon />,
    demoConfig: {
      severity: 'info',
      title: 'Heads-up',
      body: 'A highlighted notice rendered here to catch the reader’s eye.',
    },
    sampleConfig: {
      severity: 'info',
      title: '',
      body: '',
    },
    configSchema: [
      {
        key: 'severity',
        label: 'Severity',
        required: true,
        kind: 'select',
        options: [
          { value: 'info', label: 'Info (blue)' },
          { value: 'warning', label: 'Warning (yellow)' },
          { value: 'error', label: 'Error (red)' },
          { value: 'success', label: 'Success (green)' },
        ],
      },
      {
        key: 'title',
        label: 'Title',
        kind: 'string',
        placeholder: 'e.g. Scheduled maintenance',
      },
      {
        key: 'body',
        label: 'Message',
        kind: 'text',
        rows: 3,
      },
    ],
    render: renderAnnouncement,
  },

  'iframe-embed': {
    label: 'Iframe Embed',
    description:
      'Embed Confluence, Grafana, Metabase, etc. (host allow-listed).',
    galleryIcon: <WebIcon />,
    demoConfig: {
      url: 'https://kuma.bosch-digital-factory.com/status/digital-factory-status',
      title: 'Uptime Kuma (demo)',
      height: 300,
    },
    sampleConfig: {
      url: '',
      title: '',
      height: 480,
    },
    configSchema: [
      {
        key: 'url',
        label: 'URL',
        required: true,
        kind: 'url',
        description:
          'Only hosts listed in ALLOWED_HOSTS (IframeEmbed.tsx). Add new ones via PR.',
      },
      {
        key: 'title',
        label: 'Title (optional)',
        kind: 'string',
        placeholder: 'e.g. Status dashboard',
      },
      {
        key: 'height',
        label: 'Height (px)',
        kind: 'number',
        min: 120,
        max: 1200,
      },
    ],
    render: renderIframeEmbed,
  },

  'youtube-video': {
    label: 'YouTube Video',
    description:
      'Embedded YouTube video (privacy-enhanced mode). Provide the 11-character video ID.',
    galleryIcon: <OndemandVideoIcon />,
    demoConfig: {
      videoId: 'dQw4w9WgXcQ',
      title: 'Sample video',
    },
    sampleConfig: {
      videoId: '',
      title: '',
      startSeconds: 0,
    },
    configSchema: [
      {
        key: 'videoId',
        label: 'YouTube video ID (11 chars)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. dQw4w9WgXcQ',
        description:
          'Just the ID — from a URL like https://youtu.be/<id>. Avoid pasting the full URL.',
      },
      {
        key: 'title',
        label: 'Title (optional)',
        kind: 'string',
        placeholder: 'Shown in the small header above the video',
      },
      {
        key: 'startSeconds',
        label: 'Start at (seconds, optional)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderYouTubeVideo,
  },

  quote: {
    label: 'Quote',
    description:
      'Pull-quote with optional attribution. Great for mission statements and testimonials.',
    galleryIcon: <FormatQuoteIcon />,
    demoConfig: {
      text:
        'Software is built by people for people — every architectural decision is, in the end, a decision about how we want to work together.',
      author: 'Bosch Digital Factory',
      role: 'Engineering principles',
    },
    sampleConfig: {
      text: '',
      author: '',
      role: '',
    },
    configSchema: [
      {
        key: 'text',
        label: 'Quote text',
        required: true,
        kind: 'text',
        rows: 4,
        placeholder: 'The statement you want to highlight',
      },
      {
        key: 'author',
        label: 'Author (optional)',
        kind: 'string',
        placeholder: 'e.g. Jane Doe',
      },
      {
        key: 'role',
        label: 'Role / context (optional)',
        kind: 'string',
        placeholder: 'e.g. Principal Engineer, Platform team',
      },
    ],
    render: renderQuote,
  },

  image: {
    label: 'Image',
    description:
      'Image with caption (HTTPS URL or data:image base64 payload).',
    galleryIcon: <ImageIcon />,
    demoConfig: {
      src:
        'data:image/svg+xml;utf8,' +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 160">' +
            '<rect width="320" height="160" fill="#E8F1FF"/>' +
            '<rect x="40" y="40" width="240" height="80" fill="#007BC0"/>' +
            '<text x="160" y="86" font-family="sans-serif" font-size="18" fill="#fff" text-anchor="middle">Sample image</text>' +
            '</svg>',
        ),
      alt: 'Sample image placeholder',
      caption: 'Caption text appears here.',
      maxWidth: 320,
    },
    sampleConfig: {
      src: '',
      alt: '',
      caption: '',
      maxWidth: 720,
    },
    configSchema: [
      {
        key: 'src',
        label: 'URL (HTTPS or data:image)',
        required: true,
        kind: 'url',
      },
      {
        key: 'alt',
        label: 'Alt text (accessibility)',
        kind: 'string',
        placeholder: 'e.g. Architecture diagram for service X',
      },
      {
        key: 'caption',
        label: 'Caption',
        kind: 'string',
      },
      {
        key: 'maxWidth',
        label: 'Max width (px)',
        kind: 'number',
        min: 120,
        max: 1600,
      },
    ],
    render: renderImageBlock,
  },

  'team-members': {
    label: 'Team Members',
    description:
      'Live list of members of a Group entity (avatars + names, pulled from the catalog).',
    galleryIcon: <PeopleIcon />,
    demoConfig: {
      groupRef: 'group:default/devops',
      title: 'DevOps team',
      showEmail: false,
    },
    sampleConfig: {
      groupRef: 'group:default/',
      title: '',
      showEmail: false,
    },
    configSchema: [
      {
        key: 'groupRef',
        label: 'Group reference',
        required: true,
        kind: 'string',
        placeholder: 'group:default/devops',
        description:
          'Catalog ref for a Group entity. Members come from its `hasMember` relations.',
      },
      {
        key: 'title',
        label: 'Card title (optional)',
        kind: 'string',
        placeholder: 'Defaults to the group display name',
      },
      {
        key: 'showEmail',
        label: 'Show email under each name',
        kind: 'boolean',
      },
    ],
    render: renderTeamMembers,
  },

  'entity-list': {
    label: 'Entity List',
    description:
      'Live list of catalog entities (Components / APIs / Systems). Ownership is fixed to Digital Factory — filter by kind or type only.',
    galleryIcon: <AssessmentIcon />,
    demoConfig: {
      title: 'Recent Components',
      kind: 'Component',
      limit: 5,
    },
    sampleConfig: {
      title: '',
      kind: 'Component',
      type: '',
      limit: 10,
    },
    configSchema: [
      {
        key: 'title',
        label: 'Card title',
        kind: 'string',
        placeholder: 'e.g. Components owned by my team',
      },
      {
        key: 'kind',
        label: 'Kind',
        required: true,
        kind: 'select',
        options: [
          { value: 'Component', label: 'Component' },
          { value: 'API', label: 'API' },
          { value: 'System', label: 'System' },
          { value: 'Resource', label: 'Resource' },
          { value: 'Group', label: 'Group' },
          { value: 'User', label: 'User' },
        ],
      },
      {
        key: 'type',
        label: 'spec.type (optional)',
        kind: 'string',
        placeholder: 'e.g. service, library, openapi…',
      },
      {
        key: 'limit',
        label: 'Max items',
        kind: 'number',
        min: 1,
        max: 50,
      },
    ],
    render: renderEntityList,
  },

  /* ─────────────────────────────────────────────────────────────────
   * Live API widgets
   * ─────────────────────────────────────────────────────────────────
   * Every widget below shares the same data-source contract:
   *   - `url`         → fetch JSON from this endpoint. The recommended
   *                     form is `/api/proxy/<name>/<path>`, which
   *                     routes through Backstage's proxy plugin so
   *                     auth secrets stay on the backend and CORS
   *                     headaches disappear.
   *   - `inlineJson`  → paste a JSON literal directly. Useful for
   *                     prototyping a dashboard BEFORE the upstream
   *                     API exists, or for fixed reference data.
   *   - `path`        → dot-notation drill-in (e.g. `data.items.0`).
   *   - `refreshSeconds` (optional) → poll the URL at this cadence,
   *                                   minimum 5s.
   *
   * For the palette demo we ALWAYS use `inlineJson` so the demo
   * renders deterministically without hitting the network.
   */

  'api-stat-card': {
    label: 'API Stat Card',
    description:
      'Live KPI from an API endpoint (or inline JSON). Same visual as Stat Card, dynamic value.',
    galleryIcon: <CloudQueueIcon />,
    demoConfig: {
      label: 'API: ACTIVE PIPELINES',
      inlineJson: '{"active":42,"trend":"+5 last hour"}',
      valuePath: 'active',
      deltaPath: 'trend',
      tone: 'success',
    },
    sampleConfig: {
      label: '',
      url: '/api/proxy/',
      inlineJson: '',
      valuePath: '',
      deltaPath: '',
      unit: '',
      tone: 'neutral',
      refreshSeconds: 0,
    },
    configSchema: [
      {
        key: 'label',
        label: 'Label (UPPERCASE)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. ACTIVE PIPELINES',
      },
      {
        key: 'url',
        label: 'API URL',
        kind: 'url',
        placeholder: '/api/proxy/<name>/path  (recommended) or https://…',
        description:
          'Leave empty to fall back to "Inline JSON" below. Same-origin /api/proxy/* paths route through Backstage with auth.',
      },
      {
        key: 'inlineJson',
        label: 'Inline JSON (when URL is empty)',
        kind: 'text',
        rows: 4,
        placeholder: '{"active": 42, "trend": "+5"}',
      },
      {
        key: 'valuePath',
        label: 'Value path (dot notation)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. data.count or active',
      },
      {
        key: 'deltaPath',
        label: 'Delta path (optional)',
        kind: 'string',
        placeholder: 'e.g. data.trend',
      },
      {
        key: 'unit',
        label: 'Unit (optional)',
        kind: 'string',
        placeholder: 'e.g. svc, %, ms',
      },
      {
        key: 'tone',
        label: 'Tone (colour of the delta pill)',
        kind: 'select',
        options: [
          { value: 'neutral', label: 'Neutral (gray)' },
          { value: 'success', label: 'Success (green)' },
          { value: 'warning', label: 'Warning (yellow)' },
          { value: 'error', label: 'Error (red)' },
        ],
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderApiStatCard,
  },

  'api-json-viewer': {
    label: 'API JSON Viewer',
    description:
      'Pretty-printed JSON response from an API (or inline payload). Great for debugging integrations.',
    galleryIcon: <DataUsageIcon />,
    demoConfig: {
      title: 'Sample API response',
      inlineJson:
        '{\n  "status": "ok",\n  "data": {\n    "items": [\n      {"id": 1, "name": "alpha"},\n      {"id": 2, "name": "beta"}\n    ]\n  }\n}',
      path: '',
    },
    sampleConfig: {
      title: '',
      url: '/api/proxy/',
      inlineJson: '',
      path: '',
      refreshSeconds: 0,
    },
    configSchema: [
      {
        key: 'title',
        label: 'Card title (optional)',
        kind: 'string',
      },
      {
        key: 'url',
        label: 'API URL',
        kind: 'url',
        placeholder: '/api/proxy/<name>/path',
      },
      {
        key: 'inlineJson',
        label: 'Inline JSON (when URL is empty)',
        kind: 'text',
        rows: 8,
        placeholder: '{ ... paste any JSON ... }',
      },
      {
        key: 'path',
        label: 'Path to drill into (optional)',
        kind: 'string',
        placeholder: 'e.g. data.items',
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderApiJsonViewer,
  },

  'api-table': {
    label: 'API Table',
    description:
      'Table of rows from a JSON array (API or inline). Columns map dot-paths to header labels.',
    galleryIcon: <TableChartIcon />,
    demoConfig: {
      title: 'Recent deploys',
      inlineJson:
        '[{"service":"checkout","env":"prod","status":"success","time":"14:22"},{"service":"auth","env":"prod","status":"failed","time":"13:58"},{"service":"orders","env":"staging","status":"success","time":"13:41"}]',
      path: '',
      columns: [
        { key: 'service', label: 'Service' },
        { key: 'env', label: 'Environment' },
        { key: 'status', label: 'Status' },
        { key: 'time', label: 'When' },
      ],
    },
    sampleConfig: {
      title: '',
      url: '/api/proxy/',
      inlineJson: '',
      path: '',
      columns: [],
      maxRows: 100,
      refreshSeconds: 0,
    },
    configSchema: [
      {
        key: 'title',
        label: 'Card title (optional)',
        kind: 'string',
      },
      {
        key: 'url',
        label: 'API URL',
        kind: 'url',
        placeholder: '/api/proxy/<name>/path',
      },
      {
        key: 'inlineJson',
        label: 'Inline JSON (array, when URL is empty)',
        kind: 'text',
        rows: 6,
        placeholder: '[{"id":1, ...}, {"id":2, ...}]',
      },
      {
        key: 'path',
        label: 'Path to the row array (optional)',
        kind: 'string',
        placeholder: 'e.g. data.items',
        description:
          'Must resolve to an array. Each item becomes one row.',
      },
      {
        key: 'columns',
        label: 'Columns',
        kind: 'list',
        itemLabel: 'Column',
        itemSchema: [
          {
            key: 'key',
            label: 'JSON path (per row)',
            required: true,
            kind: 'string',
            placeholder: 'e.g. user.name',
          },
          {
            key: 'label',
            label: 'Header label',
            kind: 'string',
            placeholder: 'Defaults to the path',
          },
        ],
        itemSample: { key: '', label: '' },
      },
      {
        key: 'maxRows',
        label: 'Max rows rendered',
        kind: 'number',
        min: 1,
        max: 1000,
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderApiTable,
  },

  'api-bar-chart': {
    label: 'API Bar Chart',
    description:
      'Bar chart from a JSON array. Configure xKey + one or more series; supports stacked mode.',
    galleryIcon: <BarChartIcon />,
    demoConfig: {
      title: 'Deploys per environment (last 7 days)',
      inlineJson:
        '[{"env":"dev","success":32,"failed":3},{"env":"staging","success":18,"failed":1},{"env":"prod","success":7,"failed":0}]',
      xKey: 'env',
      series: [
        { dataKey: 'success', label: 'Success' },
        { dataKey: 'failed', label: 'Failed' },
      ],
      stacked: false,
    },
    sampleConfig: {
      title: '',
      url: '/api/proxy/',
      inlineJson: '',
      path: '',
      xKey: '',
      series: [],
      stacked: false,
      refreshSeconds: 0,
    },
    configSchema: [
      { key: 'title', label: 'Card title (optional)', kind: 'string' },
      {
        key: 'url',
        label: 'API URL',
        kind: 'url',
        placeholder: '/api/proxy/<name>/path',
      },
      {
        key: 'inlineJson',
        label: 'Inline JSON (array, when URL is empty)',
        kind: 'text',
        rows: 6,
        placeholder: '[{"x":"...","y":42}, ...]',
      },
      {
        key: 'path',
        label: 'Path to the row array (optional)',
        kind: 'string',
        placeholder: 'e.g. data.points',
      },
      {
        key: 'xKey',
        label: 'X-axis path (per row)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. env or timestamp',
      },
      {
        key: 'series',
        label: 'Series (one bar group per item)',
        kind: 'list',
        itemLabel: 'Series',
        itemSchema: [
          {
            key: 'dataKey',
            label: 'Value path (per row)',
            required: true,
            kind: 'string',
            placeholder: 'e.g. success',
          },
          {
            key: 'label',
            label: 'Legend label',
            kind: 'string',
          },
          { key: 'color', label: 'Override colour', kind: 'colorPicker' },
        ],
        itemSample: { dataKey: '', label: '', color: '' },
      },
      {
        key: 'stacked',
        label: 'Stack series on top of each other',
        kind: 'boolean',
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderApiBarChart,
  },

  'api-line-chart': {
    label: 'API Line Chart',
    description:
      'Line / area chart for time series and trends. Multi-series, filled or smooth modes.',
    galleryIcon: <ShowChartIcon />,
    demoConfig: {
      title: 'Latency p95 (ms)',
      inlineJson:
        '[{"t":"00:00","api":120,"web":80},{"t":"04:00","api":110,"web":75},{"t":"08:00","api":160,"web":105},{"t":"12:00","api":210,"web":140},{"t":"16:00","api":180,"web":120},{"t":"20:00","api":145,"web":95}]',
      xKey: 't',
      series: [
        { dataKey: 'api', label: 'API' },
        { dataKey: 'web', label: 'Web' },
      ],
      filled: true,
      smooth: false,
    },
    sampleConfig: {
      title: '',
      url: '/api/proxy/',
      inlineJson: '',
      path: '',
      xKey: '',
      series: [],
      filled: false,
      smooth: false,
      refreshSeconds: 0,
    },
    configSchema: [
      { key: 'title', label: 'Card title (optional)', kind: 'string' },
      {
        key: 'url',
        label: 'API URL',
        kind: 'url',
        placeholder: '/api/proxy/<name>/path',
      },
      {
        key: 'inlineJson',
        label: 'Inline JSON (array, when URL is empty)',
        kind: 'text',
        rows: 6,
        placeholder: '[{"t":"...","value":42}, ...]',
      },
      {
        key: 'path',
        label: 'Path to the row array (optional)',
        kind: 'string',
        placeholder: 'e.g. data.points',
      },
      {
        key: 'xKey',
        label: 'X-axis path (per row)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. t or timestamp',
      },
      {
        key: 'series',
        label: 'Series (one line per item)',
        kind: 'list',
        itemLabel: 'Series',
        itemSchema: [
          {
            key: 'dataKey',
            label: 'Value path (per row)',
            required: true,
            kind: 'string',
            placeholder: 'e.g. latency_ms',
          },
          { key: 'label', label: 'Legend label', kind: 'string' },
          { key: 'color', label: 'Override colour', kind: 'colorPicker' },
        ],
        itemSample: { dataKey: '', label: '', color: '' },
      },
      {
        key: 'filled',
        label: 'Filled area under each line',
        kind: 'boolean',
      },
      {
        key: 'smooth',
        label: 'Hide dots (smoother high-frequency series)',
        kind: 'boolean',
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderApiLineChart,
  },

  'api-pie-chart': {
    label: 'API Pie Chart',
    description:
      'Pie / donut for distributions: components per kind, incidents per severity, etc.',
    galleryIcon: <PieChartIcon />,
    demoConfig: {
      title: 'Components by kind',
      inlineJson:
        '[{"kind":"service","count":42},{"kind":"library","count":28},{"kind":"website","count":11},{"kind":"openapi","count":7}]',
      labelKey: 'kind',
      valueKey: 'count',
      donut: true,
    },
    sampleConfig: {
      title: '',
      url: '/api/proxy/',
      inlineJson: '',
      path: '',
      labelKey: '',
      valueKey: '',
      donut: false,
      refreshSeconds: 0,
    },
    configSchema: [
      { key: 'title', label: 'Card title (optional)', kind: 'string' },
      {
        key: 'url',
        label: 'API URL',
        kind: 'url',
        placeholder: '/api/proxy/<name>/path',
      },
      {
        key: 'inlineJson',
        label: 'Inline JSON (array, when URL is empty)',
        kind: 'text',
        rows: 6,
        placeholder: '[{"label":"...","value":42}, ...]',
      },
      {
        key: 'path',
        label: 'Path to the row array (optional)',
        kind: 'string',
        placeholder: 'e.g. data.slices',
      },
      {
        key: 'labelKey',
        label: 'Label path (per row)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. category',
      },
      {
        key: 'valueKey',
        label: 'Value path (per row, numeric)',
        required: true,
        kind: 'string',
        placeholder: 'e.g. count',
      },
      {
        key: 'donut',
        label: 'Render as a donut (inner hole)',
        kind: 'boolean',
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderApiPieChart,
  },

  /* ─────────────────────────────────────────────────────────────────
   * Platform feature widgets
   * ─────────────────────────────────────────────────────────────────
   * Surface the in-house Notifications subsystem directly in content
   * pages so a landing page or team space can show the same inbox that
   * lives on the dedicated page, without navigating away.
   *
   * Calls `/api/notifications/*` via the auth-aware fetchApi.
   */

  'recent-notifications': {
    label: 'Recent Notifications',
    description:
      'Latest items from the signed-in user\'s notifications inbox (same data as the header bell).',
    galleryIcon: <NotificationsActiveIcon />,
    demoConfig: {
      title: 'Recent notifications',
      limit: 5,
      unreadOnly: false,
      showSeeAll: true,
    },
    sampleConfig: {
      title: '',
      limit: 5,
      unreadOnly: false,
      showSeeAll: true,
      refreshSeconds: 0,
    },
    configSchema: [
      {
        key: 'title',
        label: 'Card title (optional)',
        kind: 'string',
        placeholder: 'Defaults to "Recent notifications"',
      },
      {
        key: 'limit',
        label: 'Max items shown',
        kind: 'number',
        min: 1,
        max: 50,
      },
      {
        key: 'unreadOnly',
        label: 'Only show unread',
        kind: 'boolean',
        description:
          'When enabled, hides notifications the user has already marked read.',
      },
      {
        key: 'showSeeAll',
        label: 'Show "See all" deep link to /notifications',
        kind: 'boolean',
      },
      {
        key: 'refreshSeconds',
        label: 'Auto-refresh (seconds, 0 = off)',
        kind: 'number',
        min: 0,
        max: 86400,
      },
    ],
    render: renderRecentNotifications,
  },

  'catalog-overview': {
    label: 'Catalog Overview',
    description:
      'Live counters for Components, Systems, APIs and TechDocs.',
    galleryIcon: <DashboardIcon />,
    demoConfig: {},
    sampleConfig: {},
    configSchema: [],
    render: renderCatalogOverview,
  },

  'welcome-banner': {
    label: 'Welcome Banner',
    description: 'Banner with a personalised greeting for the signed-in user.',
    galleryIcon: <StarIcon />,
    demoConfig: {},
    sampleConfig: {},
    configSchema: [],
    render: renderWelcomeBanner,
  },

  'kinds-reference': {
    label: 'Kinds Reference',
    description:
      'Diagram and table of all catalog kinds (Group / System / Component / API / etc).',
    galleryIcon: <MenuBookIcon />,
    demoConfig: {},
    sampleConfig: {},
    configSchema: [],
    render: renderKindsReference,
  },
};

export const widgetTypes = (): string[] => Object.keys(WIDGETS);

/** Re-export so the icon picker UI in WidgetEditor can show the same
 *  list the renderer accepts. Avoids a circular import between editor
 *  and registry. */
export const iconPickerOptions = () => ICON_KEYS.map(k => ({ value: k, label: k }));
// Re-exported for the rare case a caller wants the raw options array
// without going through the helper.
export const ICON_PICKER_OPTIONS = ICON_OPTIONS;

/** Bosch palette swatches available to the `colorPicker` field. The
 *  WidgetEditor renders one button per entry; clicking sets the
 *  widget's `color` config. Values intentionally match
 *  `QuickLinkCardColor` so QuickLinkCard / LinkGrid pick them up
 *  without translation. */
export const COLOR_PICKER_OPTIONS = [
  { value: 'blue-dark', label: 'Blue dark' },
  { value: 'blue', label: 'Blue' },
  { value: 'blue-light', label: 'Blue light' },
  { value: 'green-dark', label: 'Green dark' },
  { value: 'green', label: 'Green' },
  { value: 'gray-dark', label: 'Gray dark' },
  { value: 'gray', label: 'Gray' },
];
